/**
 * SetCardsTable - Sortable table for displaying cards in a set
 *
 * Displays MinimalCard data with ownership highlighting.
 * Uses TanStack Table for sorting functionality.
 */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useMemo, useState, type ReactElement } from 'react';
import { IoCaretDown, IoCaretUp, IoCheckmark } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import CardImage from '@/components/CardImage/CardImage';
import ColorDots from '@/components/ColorDots/ColorDots';
import RarityBadge from '@/components/RarityBadge/RarityBadge';
import { parseCollectorNumberSimple } from '@/lib/sortConstants';
import type { MinimalCard } from '@/services/cardDatabase';

export interface SetCardsTableProps {
  cards: MinimalCard[];
  /** Map of scryfall_id -> owned quantity */
  ownedQuantities: Map<string, number>;
  /** Called when a card row is clicked */
  onSelectCard?: (scryfallId: string) => void;
  /** Currently selected card ID */
  selectedCardId?: string | null;
}

const columnHelper = createColumnHelper<MinimalCard>();

export default function SetCardsTable({ cards, ownedQuantities, onSelectCard, selectedCardId }: SetCardsTableProps): ReactElement {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'collector_number', desc: false }]);

  const columns = useMemo(
    () => [
      // Collector Number
      columnHelper.accessor('collector_number', {
        id: 'collector_number',
        header: '#',
        size: 60,
        sortingFn: (rowA, rowB) => {
          const a = parseCollectorNumberSimple(rowA.original.collector_number);
          const b = parseCollectorNumberSimple(rowB.original.collector_number);
          if (a === b) {
            // Secondary sort by actual string for variants like "1a", "1b"
            return rowA.original.collector_number.localeCompare(rowB.original.collector_number, undefined, { numeric: true });
          }
          return a - b;
        },
        cell: ({ getValue }) => <span className="text-silver font-mono text-xs">{getValue()}</span>,
      }),

      // Image
      columnHelper.display({
        id: 'image',
        header: '',
        size: 50,
        enableSorting: false,
        cell: ({ row }) => {
          const imageUris = row.original.image_uris;
          return (
            <CardHoverPreview imageUris={imageUris} name={row.original.name}>
              <CardImage imageUris={imageUris} name={row.original.name} size="small" className="h-11 w-8 rounded" />
            </CardHoverPreview>
          );
        },
      }),

      // Name
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ getValue }) => {
          const name = getValue();
          const parts = name.split(' // ');
          if (parts.length > 1) {
            return (
              <span className="text-pearl font-medium">
                {parts.map((part, i) => (
                  <span key={i} className="block text-sm">
                    {part}
                  </span>
                ))}
              </span>
            );
          }
          return <span className="text-pearl font-medium">{name}</span>;
        },
      }),

      // Type
      columnHelper.accessor('type_line', {
        header: 'Type',
        size: 180,
        cell: ({ getValue }) => {
          const typeLine = getValue() ?? '';
          const parts = typeLine.split(' // ');
          if (parts.length > 1) {
            return (
              <span className="text-silver text-sm" title={typeLine || undefined}>
                {parts.map((part, i) => (
                  <span key={i} className="block">
                    {part}
                  </span>
                ))}
              </span>
            );
          }
          return (
            <span className="text-silver max-w-44 truncate text-sm" title={typeLine || undefined}>
              {typeLine}
            </span>
          );
        },
      }),

      // Colors (Color Identity)
      columnHelper.accessor('color_identity', {
        header: () => <span title="Color Identity">Colors</span>,
        size: 80,
        sortingFn: (rowA, rowB) => {
          // Sort by number of colors, then alphabetically
          const aColors = rowA.original.color_identity;
          const bColors = rowB.original.color_identity;
          if (aColors.length !== bColors.length) {
            return aColors.length - bColors.length;
          }
          return aColors.join('').localeCompare(bColors.join(''));
        },
        cell: ({ getValue }) => <ColorDots colors={getValue()} />,
      }),

      // CMC
      columnHelper.accessor('cmc', {
        header: 'CMC',
        size: 60,
        cell: ({ getValue }) => {
          const cmc = getValue();
          return <span className="text-silver font-mono">{cmc}</span>;
        },
      }),

      // Rarity
      columnHelper.accessor('rarity', {
        header: 'Rarity',
        size: 90,
        sortingFn: (rowA, rowB) => {
          const order: Record<string, number> = { common: 1, uncommon: 2, rare: 3, mythic: 4, special: 5, bonus: 6 };
          const aRarity = rowA.original.rarity || 'common';
          const bRarity = rowB.original.rarity || 'common';
          return (order[aRarity] ?? 0) - (order[bRarity] ?? 0);
        },
        cell: ({ getValue }) => {
          const rarity = getValue();
          if (!rarity) {
            return <span className="text-ash">-</span>;
          }
          return <RarityBadge rarity={rarity} variant="text" />;
        },
      }),

      // Owned - use accessor with function to enable sorting
      columnHelper.accessor((row) => ownedQuantities.get(row.id) ?? 0, {
        id: 'owned',
        header: 'Owned',
        size: 70,
        sortingFn: (rowA, rowB) => {
          const qtyA = ownedQuantities.get(rowA.original.id) ?? 0;
          const qtyB = ownedQuantities.get(rowB.original.id) ?? 0;
          const ownedA = qtyA > 0;
          const ownedB = qtyB > 0;

          // Owned cards first (in ascending mode)
          if (ownedA && !ownedB) return -1;
          if (!ownedA && ownedB) return 1;

          // Both owned: higher quantity first (desc by quantity)
          if (ownedA && ownedB) {
            return qtyB - qtyA;
          }

          // Neither owned: equal
          return 0;
        },
        cell: ({ getValue }) => {
          const quantity = getValue();
          if (quantity > 0) {
            return (
              <div className="flex items-center gap-1 text-emerald-400">
                <IoCheckmark className="h-4 w-4" />
                <span className="font-mono text-sm">{quantity}</span>
              </div>
            );
          }
          return <span className="text-ash">-</span>;
        },
      }),
    ],
    [ownedQuantities],
  );

  const table = useReactTable({
    data: cards,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="border-storm overflow-x-auto rounded-lg border">
      <table className="w-full">
        <thead className="bg-slate">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();

                const handleHeaderKeyDown = (e: React.KeyboardEvent) => {
                  if (canSort && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    header.column.getToggleSortingHandler()?.(e);
                  }
                };

                return (
                  <th
                    key={header.id}
                    className={clsx(
                      'text-ash px-4 py-3 text-left text-xs font-medium tracking-wider uppercase',
                      canSort && 'hover:text-pearl cursor-pointer select-none',
                      'focus-visible:text-pearl focus-visible:outline-none',
                    )}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    onKeyDown={handleHeaderKeyDown}
                    tabIndex={canSort ? 0 : undefined}
                    role={canSort ? 'button' : undefined}
                    aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sortDir === 'asc' && <IoCaretUp className="h-3 w-3" />}
                      {sortDir === 'desc' && <IoCaretDown className="h-3 w-3" />}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="divide-storm divide-y">
          {table.getRowModel().rows.map((row) => {
            const isOwned = (ownedQuantities.get(row.original.id) ?? 0) > 0;
            const isSelected = selectedCardId === row.original.id;

            const handleKeyDown = (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelectCard?.(row.original.id);
              }
            };

            return (
              <tr
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelectCard?.(row.original.id)}
                onKeyDown={handleKeyDown}
                aria-label={`${row.original.name}${isOwned ? ', owned' : ''}`}
                className={clsx(
                  'cursor-pointer transition-colors',
                  'focus-visible:ring-aether focus-visible:ring-offset-void focus-visible:relative focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset',
                  isSelected ? 'bg-aether/10' : 'hover:bg-slate/50',
                  isOwned && 'border-l-2 border-l-emerald-500',
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
